import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Spinner,
  HStack,
  Stack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
  IconButton,
  Tooltip,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { FaSearch, FaPlus, FaArrowLeft, FaThumbtack, FaTrash, FaCalendarAlt } from 'react-icons/fa';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
// @ts-ignore
import { htmlEditButton } from 'quill-html-edit-button';
import { CustomButton, CustomInput } from '../../atoms';
import { NewsArticle } from '../../organisms/NewsSection';
import { DeleteConfirmation } from './DeletionConfirmationMolecule';
import { NewsDetailPreview } from '../../pages/NewsDetailPreview';

let isQuillModuleRegistered = false;
if (!isQuillModuleRegistered) {
  // @ts-ignore
  Quill.register('modules/htmlEditButton', htmlEditButton);
  isQuillModuleRegistered = true;
}

interface NewsAdminWorkspaceProps {
  newsCards: NewsArticle[]; // The "Initial Image" — last-saved state from parent/CDN
  onNewsArticleChange: (updatedCards: NewsArticle[]) => void; // Called AFTER successful API save
  onSaveArticle: (updatedList: NewsArticle[]) => Promise<void>; // Makes the API call; throws on failure
  settings: any;
  isSaving: boolean;
}

const isPublished = (card: NewsArticle) => card.enable === 'true';

function getPinTooltip(enabled: boolean, pinned: boolean, pinnedCount: number): string {
  if (!enabled) return 'Publish article to pin';
  if (!pinned && pinnedCount >= 6) return 'Max 6 articles can be pinned';
  if (pinned) return 'Unpin article';
  return 'Pin article';
}

const normalizeHtml = (html?: string): string => {
  if (!html) return '';
  return (
    html
      // 1. Remove physical line breaks and the indentation spaces immediately following them.
      // (This stops Quill from turning code-formatting into visual text gaps)
      .replace(/[\r\n]+\s*/g, '')
      // 2. Remove spaces strictly between closing and opening HTML tags
      .replace(/>\s+</g, '><')
      // 3. Ensure empty paragraphs render correctly in Quill
      .replace(/<p><\/p>/g, '<p><br></p>')
      .trim()
  );
};

export const NewsAdminWorkspace: React.FC<NewsAdminWorkspaceProps> = ({
  newsCards,
  onNewsArticleChange,
  onSaveArticle,
  settings,
  isSaving,
}) => {
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCardId, setSelectedCardId] = useState<string | null>(newsCards[0]?.id ?? null);
  const [isSavingArticle, setIsSavingArticle] = useState(false);
  const [isPinSaving, setIsPinSaving] = useState(false);

  // The single article currently being edited
  const [currentEdit, setCurrentEdit] = useState<NewsArticle | null>(
    newsCards[0] ? { ...newsCards[0], content: normalizeHtml(newsCards[0].content) } : null,
  );

  const [rightPanelMode, setRightPanelMode] = useState<'editor' | 'preview'>('editor');

  // ── Draft map ────────────────────────────────────────────────────────────────
  // Persists unsaved edits per article ID so switching away doesn't lose work.
  // Key = article id, Value = the in-progress NewsArticle.
  // currentEdit is the live editor state for the active card; all other drafts
  // live here. On save/delete success the entry is removed.
  const [draftMap, setDraftMap] = useState<Record<string, NewsArticle>>({});

  // True only when the user has actually changed a field on the active card.
  // Quill fires onChange on mount — we do NOT count that as a user edit.
  const [isDirty, setIsDirty] = useState(false);
  // Absorbs Quill mount-time onChange. Set true on card load, cleared on first Quill fire.
  const quillMountingRef = React.useRef(false);

  const clearDraft = (cardId: string) => {
    setDraftMap(prev => {
      const next = { ...prev };
      delete next[cardId];
      return next;
    });
    setIsDirty(false);
  };

  // Only writes to the map when the user actually made changes (dirty=true).
  const stashDraft = (cardId: string | null, edit: NewsArticle | null, dirty: boolean) => {
    if (!cardId || !edit || !dirty) return;
    setDraftMap(prev => ({ ...prev, [cardId]: edit }));
  };

  // Active card: show dot only when isDirty (user typed something).
  // Stashed cards: show dot when they have an entry in draftMap.
  //   const hasDraft = (cardId: string): boolean => {
  //     if (cardId === selectedCardId) return isDirty;
  //     return cardId in draftMap;
  //   };

  // Overlay ALL drafts (active + stashed) on top of the Initial Image so the
  // left panel always reflects the latest in-progress state of every card.
  const displayCards = useMemo(() => {
    const allDrafts: Record<string, NewsArticle> =
      selectedCardId && currentEdit ? { ...draftMap, [selectedCardId]: currentEdit } : draftMap;
    const merged = newsCards.map(c => allDrafts[c.id] ?? c);
    // New (unsaved) articles exist in draftMap but not in newsCards
    const newDraftCards = Object.values(allDrafts).filter(d => !newsCards.some(c => c.id === d.id));
    return [...newDraftCards, ...merged];
  }, [newsCards, draftMap, currentEdit, selectedCardId]);

  const quillModules = useMemo(
    () => ({
      htmlEditButton: {
        debug: false,
        msg: 'Edit HTML here...',
        okText: 'Save',
        cancelText: 'Cancel',
        buttonHTML: '\u2039/\u203A',
        buttonTitle: 'Show HTML source',
        syntax: true,
      },
    }),
    [],
  );

  const pinnedCount = displayCards.filter(c => c.enable === 'true' && c.pin === 'true').length;

  const filteredCards = displayCards.filter(card =>
    card.title?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleSelectCard = (cardId: string) => {
    if (cardId === selectedCardId) return;
    // Stash whatever is in the editor right now before switching
    stashDraft(selectedCardId, currentEdit, isDirty);
    // Restore draft if one exists, otherwise load from the Initial Image
    const draft = draftMap[cardId];
    const baseline = newsCards.find(c => c.id === cardId);
    const next =
      draft ?? (baseline ? { ...baseline, content: normalizeHtml(baseline.content) } : null);
    setSelectedCardId(cardId);
    setCurrentEdit(next);
    // New card has a draft stashed, so mark dirty; fresh load from baseline is clean
    setIsDirty(cardId in draftMap);
    quillMountingRef.current = true;
    setRightPanelMode('editor');
  };

  const handleAddArticle = () => {
    // Stash whatever is open before switching to the new blank card
    stashDraft(selectedCardId, currentEdit, isDirty);
    setIsDirty(false);
    quillMountingRef.current = true;
    const newCard: NewsArticle = {
      id: Date.now().toString(),
      title: '',
      imageUrl: '',
      createdDate: '',
      updatedDate: '',
      publishedDate: '',
      enable: 'false',
      writtenBy: '',
      pin: 'false',
      content: '',
    };
    setSelectedCardId(newCard.id);
    setCurrentEdit(newCard);
    setRightPanelMode('editor');
  };

  const handlePinToggle = async (cardId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const card = displayCards.find(c => c.id === cardId);
    if (!card || card.enable !== 'true') return;

    const alreadyPinned = card.pin === 'true';
    if (!alreadyPinned && pinnedCount >= 6) return;

    const newPin = alreadyPinned ? 'false' : 'true';
    const updatedBaseline = newsCards.map(c => (c.id === cardId ? { ...c, pin: newPin } : c));

    setIsPinSaving(true);
    try {
      await onSaveArticle(updatedBaseline);
      // ── Success: commit to parent's Initial Image and sync editor ──
      onNewsArticleChange(updatedBaseline);

      // Update the active editor if it's the pinned card
      if (currentEdit?.id === cardId) {
        setCurrentEdit(prev => (prev ? { ...prev, pin: newPin } : prev));
      }

      // Sync the stash if the card is currently stashed!
      if (draftMap[cardId]) {
        setDraftMap(prev => ({
          ...prev,
          [cardId]: { ...prev[cardId], pin: newPin },
        }));
      }

      toast({
        title: newPin === 'true' ? 'Article pinned.' : 'Article unpinned.',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right',
      });
    } catch {
      toast({
        title: 'Pin update failed',
        description: 'Could not update pin status. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsPinSaving(false);
    }
  };

  const handleDelete = async (cardId: string) => {
    const isNewArticle = !newsCards.some(c => c.id === cardId);

    // New unsaved article: no API call needed, just clear local state
    if (isNewArticle) {
      if (selectedCardId === cardId) {
        setSelectedCardId(null);
        setCurrentEdit(null);
      }
      return;
    }

    // Existing article: hit the API first, only update state on success
    setIsSavingArticle(true);
    const updatedBaseline = newsCards.filter(c => c.id !== cardId);
    try {
      await onSaveArticle(updatedBaseline);
      // ── Success: commit to parent's Initial Image then clear editor ──
      onNewsArticleChange(updatedBaseline);
      clearDraft(cardId);
      if (selectedCardId === cardId) {
        setSelectedCardId(null);
        setCurrentEdit(null);
      }
      toast({
        title: 'Article deleted.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch {
      toast({
        title: 'Delete failed',
        description: 'Could not delete the article. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsSavingArticle(false);
    }
  };

  // ── Core save logic ─────────────────────────────────────────────────────────
  // publish=true  → Update Live Article  (enable: 'true')
  // publish=false → Save as Unpublished  (enable: 'false')
  //
  // Flow:
  //   1. Build finalData from currentEdit
  //   2. Build updatedBaseline (replace/insert in newsCards)
  //   3. Await onSaveArticle(updatedBaseline) — this makes the API call
  //   4. Only on success: call onNewsArticleChange (update Initial Image in parent)
  //                       and update currentEdit to reflect saved state
  //   5. On failure: toast error, leave everything unchanged
  const saveArticle = async (publish: boolean) => {
    if (!currentEdit || !selectedCardId) return;
    setIsSavingArticle(true);

    const cleanContent = normalizeHtml(currentEdit.content);
    const now = new Date().toISOString();
    const today = now.split('T')[0];

    const finalData: NewsArticle = {
      ...currentEdit,
      content: cleanContent,
      enable: publish ? 'true' : 'false',
      // Unpublishing clears the pin
      pin: publish ? currentEdit.pin : 'false',
      updatedDate: now,
      publishedDate: publish ? currentEdit.publishedDate || today : currentEdit.publishedDate,
    };

    const existsInBaseline = newsCards.some(c => c.id === selectedCardId);
    const updatedBaseline: NewsArticle[] = existsInBaseline
      ? newsCards.map(c => (c.id === selectedCardId ? finalData : c))
      : [finalData, ...newsCards];

    try {
      // API call — throws on failure
      await onSaveArticle(updatedBaseline);

      // ── Success: commit to parent's Initial Image and local editor state ──
      onNewsArticleChange(updatedBaseline);
      setCurrentEdit(finalData);
      clearDraft(selectedCardId);

      toast({
        title: publish ? 'Article published!' : 'Saved as unpublished.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch {
      toast({
        title: 'Save failed',
        description: 'Could not save the article. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      // Do NOT update parent state or currentEdit — keep the previous good state
    } finally {
      setIsSavingArticle(false);
    }
  };

  const setField = <K extends keyof NewsArticle>(field: K, value: NewsArticle[K]) => {
    setCurrentEdit(prev => (prev ? { ...prev, [field]: value } : prev));
    setIsDirty(true);
  };

  const activelySaving = isSaving || isSavingArticle;

  return (
    <Flex height="calc(100vh - 100px)" overflow="hidden" bg="gray.50">
      {/* ── LEFT PANEL: Article List ─────────────────────────────────────── */}
      <Box
        width="380px"
        minWidth="380px"
        borderRight="1px solid"
        borderColor="gray.200"
        bg="white"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        position="relative"
      >
        {/* Pin-save overlay — blocks interaction until API call resolves */}
        {isPinSaving && (
          <Flex
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={10}
            bg="whiteAlpha.700"
            align="center"
            justify="center"
          >
            <Spinner size="md" color="blue.500" thickness="3px" speed="0.6s" />
          </Flex>
        )}
        <Box px={4} pt={4} pb={3} borderBottom="1px solid" borderColor="gray.100">
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontWeight="bold" fontSize="lg">
              News Articles
            </Text>
            <CustomButton
              icon={<FaPlus />}
              onClick={handleAddArticle}
              size="sm"
              fontSize="sm"
              fontWeight="bold"
              style={{ backgroundColor: '#19a249', color: 'white' }}
            >
              NEW ARTICLE
            </CustomButton>
          </Flex>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <FaSearch color="gray" />
            </InputLeftElement>
            <Input
              placeholder="Search articles..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              bg="gray.100"
              border="none"
              borderRadius="md"
              _focus={{ bg: 'white', boxShadow: 'outline' }}
            />
          </InputGroup>
        </Box>

        <Box px={4} py={2} borderBottom="1px solid" borderColor="gray.100">
          <Text fontSize="xs" color="gray.500" fontWeight="medium">
            Pinned: {pinnedCount} / 6
          </Text>
        </Box>

        <Box flex="1" overflowY="auto" p={2}>
          {filteredCards.length === 0 && (
            <Text fontSize="sm" color="gray.400" textAlign="center" mt={8}>
              No articles found.
            </Text>
          )}
          {filteredCards.map(card => {
            const published = isPublished(card);
            const pinned = card.pin === 'true';
            const isSelected = selectedCardId === card.id;
            const badgeColor = published ? '#2663eb' : '#f59e0c';

            return (
              <Box
                key={card.id}
                p={3}
                mb={1}
                borderRadius="lg"
                cursor="pointer"
                border="1px solid"
                borderColor={isSelected ? 'blue.100' : 'transparent'}
                bg={isSelected ? 'blue.50' : 'white'}
                _hover={{ bg: isSelected ? 'blue.50' : 'gray.50' }}
                onClick={() => handleSelectCard(card.id)}
                transition="all 0.15s"
              >
                <Flex justify="space-between" align="flex-start" mb={1}>
                  <Flex align="center" gap={1.5}>
                    <Badge
                      bg={badgeColor}
                      color="white"
                      fontSize="9px"
                      fontWeight="bold"
                      borderRadius="sm"
                      px={1.5}
                      py={0.5}
                      textTransform="uppercase"
                    >
                      {published ? 'Published' : 'Unpublished'}
                    </Badge>
                    {/* {hasDraft(card.id) && (
                      <Tooltip label="Unsaved changes" placement="top" fontSize="xs">
                        <Box w={2} h={2} borderRadius="full" bg="orange.400" flexShrink={0} />
                      </Tooltip>
                    )} */}
                  </Flex>

                  <Tooltip
                    label={getPinTooltip(card.enable === 'true', pinned, pinnedCount)}
                    placement="top"
                    fontSize="xs"
                  >
                    <Box
                      as="span"
                      onClick={(e: React.MouseEvent<Element, MouseEvent>) =>
                        handlePinToggle(card.id, e as React.MouseEvent)
                      }
                      sx={{
                        color: pinned ? '#3b83f6' : '#cbd5e1',
                        cursor:
                          card.enable !== 'true' || (!pinned && pinnedCount >= 6)
                            ? 'not-allowed'
                            : 'pointer',
                        transition: 'color 0.15s',
                        _hover: {
                          color:
                            card.enable === 'true' && (pinned || pinnedCount < 6)
                              ? '#3b83f6'
                              : undefined,
                        },
                      }}
                    >
                      <FaThumbtack size={11} />
                    </Box>
                  </Tooltip>
                </Flex>

                <Text
                  fontWeight="semibold"
                  fontSize="sm"
                  lineHeight="tight"
                  mb={1}
                  noOfLines={2}
                  color={card.title ? 'gray.800' : 'gray.400'}
                >
                  {card.title || 'Untitled Article'}
                </Text>

                <Flex align="center" gap={1} overflow="hidden" flexWrap="wrap">
                  <Box color="gray.400" flexShrink={0} mt="1px">
                    <FaCalendarAlt size={9} />
                  </Box>
                  <Text fontSize="10px" color="gray.400" flexShrink={0}>
                    {card.createdDate
                      ? new Date(card.createdDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: '2-digit',
                          year: 'numeric',
                        })
                      : 'No date set'}
                  </Text>
                  {card.updatedDate && (
                    <>
                      <Text fontSize="10px" color="gray.300" flexShrink={0} mx={0.5}>
                        ·
                      </Text>
                      <Text fontSize="10px" color="gray.400" flexShrink={0}>
                        {`Updated ${new Date(card.updatedDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: '2-digit',
                          year: 'numeric',
                        })}`}
                      </Text>
                    </>
                  )}
                  {card.writtenBy && (
                    <>
                      <Text fontSize="10px" color="gray.300" flexShrink={0} mx={0.5}>
                        ·
                      </Text>
                      <Text
                        fontSize="10px"
                        fontWeight="medium"
                        color={badgeColor}
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        minWidth={0}
                      >
                        Writer: {card.writtenBy}
                      </Text>
                    </>
                  )}
                </Flex>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── RIGHT PANEL: Editor / Preview ────────────────────────────────── */}
      <Box flex="1" display="flex" flexDirection="column" overflow="hidden" bg="white">
        {!currentEdit ? (
          <Flex
            flex="1"
            align="center"
            justify="center"
            direction="column"
            gap={3}
            color="gray.400"
          >
            <Text fontSize="sm">Select an article to edit</Text>
          </Flex>
        ) : (
          <>
            {/* Header bar */}
            <Box
              px={6}
              py={3}
              borderBottom="1px solid"
              borderColor="gray.200"
              bg="white"
              boxShadow="sm"
            >
              <Flex align="center" justify="space-between">
                <HStack spacing={3}>
                  <IconButton
                    aria-label="Back to list"
                    icon={<FaArrowLeft />}
                    size="sm"
                    variant="ghost"
                    color="gray.400"
                    onClick={() => {
                      stashDraft(selectedCardId, currentEdit, isDirty);
                      setSelectedCardId(null);
                      setCurrentEdit(null);
                    }}
                  />
                  <Box>
                    <Text fontWeight="bold" fontSize="sm" color="gray.800" lineHeight="none" mb={1}>
                      Article Editor
                    </Text>
                    <Text fontSize="10px" color="gray.400">
                      {isPublished(currentEdit) ? 'Published' : 'Unpublished'}
                    </Text>
                  </Box>
                </HStack>

                <HStack spacing={3}>
                  {/* Editor / Preview toggle */}
                  <HStack bg="gray.100" p={1} borderRadius="lg" spacing={0}>
                    {(['editor', 'preview'] as const).map(mode => (
                      <Box
                        key={mode}
                        px={3}
                        py={1.5}
                        borderRadius="md"
                        cursor="pointer"
                        bg={rightPanelMode === mode ? 'white' : 'transparent'}
                        boxShadow={rightPanelMode === mode ? 'sm' : 'none'}
                        color={rightPanelMode === mode ? '#2663eb' : 'gray.500'}
                        fontWeight="bold"
                        fontSize="xs"
                        onClick={() => setRightPanelMode(mode)}
                        transition="all 0.15s"
                        textTransform="capitalize"
                      >
                        {mode}
                      </Box>
                    ))}
                  </HStack>

                  <Divider orientation="vertical" height="24px" borderColor="gray.200" />

                  <DeleteConfirmation
                    message="Are you sure you want to delete this article?"
                    onConfirm={() => handleDelete(currentEdit.id)}
                    trigger={
                      <IconButton
                        aria-label="Delete article"
                        icon={<FaTrash />}
                        size="sm"
                        variant="ghost"
                        color="gray.400"
                        _hover={{ color: 'red.500' }}
                      />
                    }
                  />

                  {/* Save as Unpublished */}
                  <CustomButton
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    onClick={() => saveArticle(false)}
                    isLoading={activelySaving}
                    fontSize="sm"
                    fontWeight="bold"
                  >
                    Save as Unpublished
                  </CustomButton>

                  {/* Update Live Article */}
                  <CustomButton
                    size="sm"
                    onClick={() => saveArticle(true)}
                    isLoading={activelySaving}
                    style={{ backgroundColor: '#2663eb', color: 'white' }}
                    fontSize="sm"
                    fontWeight="bold"
                  >
                    Update Live Article
                  </CustomButton>
                </HStack>
              </Flex>
            </Box>

            {/* Editor panel */}
            {rightPanelMode === 'editor' && (
              <Box flex="1" overflowY="auto" px={8} py={6}>
                <Box maxWidth="800px" mx="auto">
                  <Stack spacing={6}>
                    <Flex gap={4} align="flex-start">
                      <Box flex="1">
                        <Text
                          fontSize="10px"
                          fontWeight="bold"
                          color="gray.400"
                          textTransform="uppercase"
                          mb={1}
                        >
                          Article Title
                        </Text>
                        <Input
                          value={currentEdit.title || ''}
                          onChange={e => setField('title', e.target.value)}
                          placeholder="Enter article title..."
                          fontSize="lg"
                          fontWeight="semibold"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="lg"
                          px={4}
                          py={3}
                          _focus={{ borderColor: 'blue.400', boxShadow: 'outline' }}
                        />
                      </Box>
                      <Box
                        minWidth="200px"
                        p={4}
                        border="1px solid"
                        borderColor="blue.100"
                        borderRadius="xl"
                        bg="blue.50"
                      >
                        <Text
                          fontSize="10px"
                          fontWeight="bold"
                          color="#2663eb"
                          textTransform="uppercase"
                          mb={3}
                        >
                          Publishing Control
                        </Text>
                        <Tooltip label="Coming Soon" placement="right" fontSize="xs">
                          <Flex align="center" gap={2} opacity={0.45} cursor="not-allowed" mb={2}>
                            <Box
                              w={4}
                              h={4}
                              borderRadius="full"
                              border="2px solid"
                              borderColor="gray.300"
                              flexShrink={0}
                            />
                            <Text fontSize="sm" color="gray.500">
                              Publish Immediately
                            </Text>
                          </Flex>
                        </Tooltip>
                        <Tooltip label="Coming Soon" placement="right" fontSize="xs">
                          <Flex align="center" gap={2} opacity={0.45} cursor="not-allowed">
                            <Box
                              w={4}
                              h={4}
                              borderRadius="full"
                              border="2px solid"
                              borderColor="gray.300"
                              flexShrink={0}
                            />
                            <Text fontSize="sm" color="gray.500">
                              Schedule for later
                            </Text>
                          </Flex>
                        </Tooltip>
                      </Box>
                    </Flex>

                    <HStack spacing={4} align="flex-start">
                      <Box flex="1">
                        <Text
                          fontSize="10px"
                          fontWeight="bold"
                          color="gray.400"
                          textTransform="uppercase"
                          mb={1}
                        >
                          Header Image URL
                        </Text>
                        <CustomInput
                          value={currentEdit.imageUrl || ''}
                          onChange={e => setField('imageUrl', e.target.value)}
                          placeholder="https://..."
                          width="100%"
                        />
                      </Box>
                      <Box flex="1">
                        <Text
                          fontSize="10px"
                          fontWeight="bold"
                          color="gray.400"
                          textTransform="uppercase"
                          mb={1}
                        >
                          Author / Credits
                        </Text>
                        <CustomInput
                          value={currentEdit.writtenBy || ''}
                          onChange={e => setField('writtenBy', e.target.value)}
                          placeholder="Author name"
                          width="100%"
                        />
                      </Box>
                    </HStack>

                    <HStack spacing={4} align="flex-start">
                      <Box flex="1">
                        <Text
                          fontSize="10px"
                          fontWeight="bold"
                          color="gray.400"
                          textTransform="uppercase"
                          mb={1}
                        >
                          Created Date{' '}
                          <Text
                            as="span"
                            fontSize="9px"
                            color="gray.300"
                            fontWeight="normal"
                            textTransform="none"
                          >
                            (optional)
                          </Text>
                        </Text>
                        <CustomInput
                          value={currentEdit.createdDate || ''}
                          onChange={e => setField('createdDate', e.target.value)}
                          placeholder="Date"
                          type="date"
                          width="100%"
                        />
                      </Box>
                      <Box flex="1">
                        <Text
                          fontSize="10px"
                          fontWeight="bold"
                          color="gray.400"
                          textTransform="uppercase"
                          mb={1}
                        >
                          Updated Date
                        </Text>
                        <Box
                          px={3}
                          py={2}
                          border="1px solid"
                          borderColor="gray.100"
                          borderRadius="md"
                          bg="gray.50"
                        >
                          <Text
                            fontSize="sm"
                            color={currentEdit.updatedDate ? 'gray.600' : 'gray.300'}
                          >
                            {currentEdit.updatedDate
                              ? new Date(currentEdit.updatedDate).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: '2-digit',
                                  year: 'numeric',
                                })
                              : 'Auto-set on save'}
                          </Text>
                        </Box>
                      </Box>
                    </HStack>

                    <Box>
                      <Text
                        fontSize="10px"
                        fontWeight="bold"
                        color="gray.400"
                        textTransform="uppercase"
                        mb={1}
                      >
                        Body Content
                      </Text>
                      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                      <Box
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="xl"
                        overflow="hidden"
                        boxShadow="sm"
                        onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
                        sx={{
                          '.ql-editor': {
                            backgroundColor: 'white',
                            minHeight: '300px',
                            maxHeight: '500px',
                            overflowY: 'auto',
                            padding: '1rem',
                            whiteSpace: 'normal !important',
                          },
                          '.ql-toolbar .ql-formats button.ql-htmlEditButton': {
                            width: 'auto !important',
                            minWidth: '50px !important',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'black',
                            fontWeight: 'bold',
                          },
                        }}
                      >
                        <ReactQuill
                          value={currentEdit.content || ''}
                          onChange={val => {
                            if (quillMountingRef.current) {
                              // Mount-time synthetic onChange — absorb silently, keep content in sync
                              quillMountingRef.current = false;
                              setCurrentEdit(prev => (prev ? { ...prev, content: val } : prev));
                              return;
                            }
                            setField('content', val);
                          }}
                          theme="snow"
                          modules={quillModules}
                        />
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            )}

            {/* Preview panel */}
            {rightPanelMode === 'preview' && (
              <Box flex="1" overflowY="auto" bg="gray.100" p={4}>
                <Box bg="white" borderRadius="lg" overflow="auto" boxShadow="xl" height="100%">
                  <NewsDetailPreview
                    newsId={currentEdit.id}
                    newsArticles={displayCards}
                    settings={settings}
                    onContactUsClick={() => {}}
                    onClickMyTickets={() => {}}
                    onClickLandingPageButton={() => {}}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Flex>
  );
};
